import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["card"]

  connect() {
    this.isTransitioning = false;
    this.prefetchedData = null;
  }

  // Preload page + images on hover so they're cached by click time
  async prefetch(event) {
    const link = event.currentTarget.closest("a") || event.currentTarget;
    const url = link.href;

    // Don't re-prefetch the same URL
    if (this.prefetchedData && this.prefetchedData.url === url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, "text/html");
      const newContent = doc.querySelector(".kids-page");
      if (!newContent) return;

      // Preload all images into browser cache
      const imgUrls = Array.from(newContent.querySelectorAll("img"))
        .map((img) => img.src || img.getAttribute("data-src"))
        .filter(Boolean);

      imgUrls.forEach((src) => {
        const img = new Image();
        img.src = src;
      });

      this.prefetchedData = { url, html };
    } catch (_e) {
      // Silent fail — click will fetch fresh
    }
  }

  async navigate(event) {
    // Allow modifier-clicks to open in new tab
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;

    event.preventDefault();

    // Guard against double-click
    if (this.isTransitioning) return;
    this.isTransitioning = true;

    const link = event.currentTarget.closest("a") || event.currentTarget;
    const url = link.href;
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // Start fading old page immediately — instant visual feedback
    const oldPage = this.element;
    oldPage.classList.add("kids-page--light-off");

    try {
      // Use prefetched HTML if available, otherwise fetch fresh
      let html;
      if (this.prefetchedData && this.prefetchedData.url === url) {
        html = this.prefetchedData.html;
        this.prefetchedData = null;
      } else {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        html = await response.text();
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const newContent = doc.querySelector(".kids-page");
      if (!newContent) throw new Error("Missing .kids-page in response");

      window.scrollTo(0, 0);

      const parent = oldPage.parentElement;

      if (reducedMotion) {
        parent.replaceChild(newContent, oldPage);
        history.pushState({ kidsTransition: true }, "", url);
        this.setupPopstateHandler();
        this.isTransitioning = false;
        return;
      }

      // Insert new page before old page, boost resource cards (hidden until images load)
      newContent.classList.add("kids-page--transitioning");
      parent.insertBefore(newContent, oldPage);

      // Reveal cards once all images loaded (or 100ms safety timeout)
      this.waitForImages(newContent).then(() => {
        newContent.classList.add("kids-page--cards-ready");
      });

      // Parent becomes stacking context root with yellow bg, clipped so curtain doesn't spill
      parent.style.position = "relative";
      parent.style.zIndex = "0";
      parent.style.backgroundColor = "#FFFEEB";
      parent.style.overflow = "hidden";

      // Old page: absolute overlay, in front of hero/tabs but behind resources
      // Use the taller of old page or new content so curtain covers entire panel area
      const fullHeight = Math.max(oldPage.offsetHeight, newContent.offsetHeight);
      oldPage.style.position = "absolute";
      oldPage.style.top = "0";
      oldPage.style.left = "0";
      oldPage.style.width = "100%";
      oldPage.style.height = `${fullHeight}px`;
      oldPage.style.margin = "0";
      oldPage.style.zIndex = "-1";

      // Measure tab bar position for drop distance
      const tabBar = newContent.querySelector(".kids-topic-tabs__tab-bar");
      const parentTop = parent.getBoundingClientRect().top;
      const curtainTop = tabBar
        ? tabBar.getBoundingClientRect().bottom - parentTop
        : 400;

      // Set drop distance CSS variable
      oldPage.style.setProperty("--curtain-drop", `${curtainTop}px`);

      // Single rAF to commit layout, then immediately start animation
      await new Promise((r) => requestAnimationFrame(r));

      // Start curtain drop (light-off already started on click)
      oldPage.classList.add("kids-page--dropping");

      // Wait for drop animation to finish (with safety timeout)
      await this.animEnd(oldPage, 1800);

      // === Cleanup ===
      oldPage.remove();
      newContent.classList.remove("kids-page--transitioning");
      parent.style.position = "";
      parent.style.zIndex = "";
      parent.style.backgroundColor = "";
      parent.style.overflow = "";
      history.pushState({ kidsTransition: true }, "", url);
      this.setupPopstateHandler();
      this.isTransitioning = false;
    } catch (_err) {
      // Fallback to normal navigation
      this.isTransitioning = false;
      window.location.href = url;
    }
  }

  setupPopstateHandler() {
    window.addEventListener("popstate", () => window.location.reload(), {
      once: true,
    });
  }

  sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  waitForImages(container) {
    const imgs = Array.from(container.querySelectorAll("img"));
    if (imgs.length === 0) return Promise.resolve();

    const loaded = imgs.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise((r) => {
            img.addEventListener("load", r, { once: true });
            img.addEventListener("error", r, { once: true });
          })
    );

    // Safety timeout so cards never stay hidden forever
    const safety = this.sleep(100);
    return Promise.race([Promise.all(loaded), safety]);
  }

  animEnd(el, timeout = 0) {
    return new Promise((r) => {
      let resolved = false;
      const resolve = () => {
        if (resolved) return;
        resolved = true;
        r();
      };
      el.addEventListener("animationend", resolve, { once: true });
      if (timeout > 0) setTimeout(resolve, timeout);
    });
  }
}
